import _ from 'lodash';

import {TruncationType} from '../../components/common/TruncateText/TruncateTextTypes';
import {
  XAxisType,
  YAxisType,
} from '../../components/WorkspaceDrawer/Settings/types';
import {RunsData} from '../../containers/RunsDataLoader';
import {ChartAggOption} from '../../generated/graphql';
import {RunWithRunsetInfo} from '../../state/runs/types';
import {CustomRunNames} from '../../state/views/customRunNames/types';
import * as ColorUtil from '../colors';
import {ColorPaletteOption} from '../colors';
import {
  configKeysInExpression,
  evaluateExpression,
  Expression,
  expressionToString,
  metricsInExpression,
  summaryKeysInExpression,
} from '../expr';
import {
  legendTemplateRemoveCrosshairValues,
  legendTemplateToFancyLegendProps,
  parseLegendTemplate,
} from '../legend';
import * as Run from '../runs';
import {keyToString} from '../runs';
import * as RunTypes from '../runTypes';
import {RunColorConfig} from '../section';
import {textWidth} from '../text';
import {
  axisLabelMargin,
  formatYAxis,
  getScaleFnFromScaleObject,
  getYAxisWidth,
} from './axis';
import type {Chart} from './chartTypes';
import * as PlotMath from './math';
import {PlotFontSize, plotFontSizeToPx} from './plotFontSize';
import {prettifyMetricName} from './prettifyMetricName';
import {AggregateCalculation, Bar, Line, RunSetInfo, Scalar} from './types';

const DEFAULT_VIOLIN_PLOT_BINS = 10;

export function defaultRunCharts(metricNames: [string]) {
  let x = 0;
  let y = 0;
  const importantRegexes = [/loss/, /accuracy|acc$/, /n_success/];
  const charts: Chart[] = [];
  importantRegexes.forEach(regex => {
    const lines = metricNames.filter(n => n.match(regex));
    if (lines.length > 0) {
      charts.push({
        config: {lines},
        layout: {x, y, w: 6, h: 2},
      });
      x += 6;
      if (x === 12) {
        x = 0;
        y += 2;
      }
    }
  });
  if (charts.length === 1) {
    charts[0].layout.w = 12;
  }
  return charts;
}

export function filterLines(lines: Line[], xScale?: 'log' | 'linear') {
  return lines.map(line => {
    const dataWithFinitePoints = line.data.filter(
      pt => _.isFinite(pt.x) && _.isFinite(pt.y)
    );
    line.data =
      xScale === 'log'
        ? dataWithFinitePoints.filter(pt => pt.x > 0)
        : dataWithFinitePoints;
    return line;
  });
}

/**
 * I don't full understand what this is doing for log charts [needs investigation]
 */
export function filterFullFidelityLines(
  lines: Line[],
  xScale?: 'log' | 'linear'
) {
  return lines.map(line => {
    line.data =
      xScale === 'log'
        ? line.data.filter(pt => _.isFinite(pt.x) && pt.x > 0)
        : line.data;
    return line;
  });
}

export function filterNegative(lines: Line[]) {
  /**
   * Iterate over all the lines and remove non-positive values for log scale
   */
  // TODO: Check if the NaN works ok
  // TODO: This doesn't handle area graphs
  return lines.map((line, i) => {
    const newLine = line;
    newLine.data = line.data.filter(point => point.y > 0);
    return newLine;
  });
}

export function isMonotonicIncreasing(arr: number[]) {
  const n = arr.length;
  let i = 1;
  while (i < n && arr[i] - arr[i - 1] >= 0) {
    i++;
  }
  return i === n;
}

interface RunDataPoint {
  run: RunWithRunsetInfo;
  value: number;
  metricName: string;
  runOrGroupUniqueId: string;
  runOrGroupDisplayName: string;
  customRunName?: string;
  runNameTruncationType?: TruncationType;
}

type GroupDataPoint = RunDataPoint & {
  groupKeys: RunTypes.Key[];
  stddev?: number;
  stderr?: number;
  mean?: number;
  quartiles?: [number, number, number, number, number];
  bins?: Array<{bin: number; count: number}>;
  range?: [number, number];
};

function isGroupDataPoint(
  dataPoint: GroupDataPoint | RunDataPoint
): dataPoint is GroupDataPoint {
  return (dataPoint as GroupDataPoint).groupKeys != null;
}

function aggregatePoints(
  points: RunDataPoint[],
  aggregateCalculations: AggregateCalculation[], // currently we do all calculations
  groupAgg: ChartAggOption,
  groupArea: AggregateCalculation,
  groupKeys: RunTypes.Key[],
  numBins?: number,
  runNameTruncationType?: TruncationType
): GroupDataPoint | null {
  if (points.length === 0) {
    return null;
  }
  const values = points.map(p => p.value);
  const meanVal = PlotMath.avg(values);
  const stddevVal = PlotMath.stddev(values);
  const stderrVal = PlotMath.stderr(values);
  const quartilesVal = PlotMath.quartiles(values);
  const medianVal = quartilesVal[2];

  const bins = numBins != null ? PlotMath.bin(values, numBins) : undefined;

  let runIndex = 0;
  if (groupAgg === 'min') {
    runIndex = PlotMath.argMin(values);
  } else if (groupAgg === 'max') {
    runIndex = PlotMath.argMax(values);
  }

  const value =
    groupAgg === 'mean'
      ? meanVal
      : groupAgg === 'median'
        ? medianVal
        : groupAgg === 'max'
          ? quartilesVal[4]
          : groupAgg === 'min'
            ? quartilesVal[0]
            : groupAgg === 'sum'
              ? _.sum(values)
              : 0;

  const area: [number, number] | undefined =
    groupArea === 'minmax'
      ? [quartilesVal[0], quartilesVal[4]]
      : groupArea === 'stddev'
        ? [value - stddevVal, value + stddevVal]
        : groupArea === 'stderr'
          ? [value - stderrVal, value + stderrVal]
          : undefined;

  return {
    run: points[runIndex].run, // max or min case
    metricName: points[runIndex].metricName, // max or min case
    runOrGroupUniqueId: points[runIndex].runOrGroupUniqueId,
    runOrGroupDisplayName: points[runIndex].runOrGroupDisplayName,
    value,
    mean: meanVal,
    stddev: stddevVal,
    stderr: stderrVal,
    quartiles: quartilesVal,
    range: area,
    bins,
    groupKeys,
    runNameTruncationType,
  };
}

type PointsFromRunsetParams = {
  runs: RunWithRunsetInfo[];
  metrics: RunTypes.Key[]; // typically one metric otherwise will aggregate across metrics
  expressions?: Expression[];
  groupKeys: RunTypes.Key[];
  aggregateCalculations: AggregateCalculation[];
  legendTemplate?: string;
  groupAgg?: ChartAggOption;
  groupArea?: AggregateCalculation;
  boxPlot?: boolean;
  violinPlot?: boolean;
  mergeRunsets?: boolean;
  customRunNames?: CustomRunNames;
  runNameTruncationType?: TruncationType;
};

function pointsFromRunset(props: PointsFromRunsetParams) {
  const {
    runs,
    metrics,
    expressions,
    groupKeys,
    aggregateCalculations,
    groupAgg,
    groupArea,
    violinPlot,
    mergeRunsets,
    customRunNames,
    runNameTruncationType,
  } = props;
  /*
   * Converts data in runset format to point format
   * Also does aggregation
   */
  const expressionKeys =
    expressions != null
      ? expressions.flatMap(expr =>
          _.concat(summaryKeysInExpression(expr), configKeysInExpression(expr))
        )
      : [];

  let barData: RunDataPoint[] = runs
    .map(run => {
      const customRunName = Run.getCustomRunName(
        run.name,
        groupKeys.length > 0,
        customRunNames
      );
      if (expressions && expressions.length > 0) {
        const metricsToValue: {[key: string]: number} = {};
        expressionKeys.forEach(
          exprKey =>
            (metricsToValue[keyToString(exprKey)] = Run.getValueSafe(
              run,
              exprKey
            ) as number)
        );
        return expressions.map(expr => {
          const val = evaluateExpression(expr, metricsToValue);
          return {
            metricName: expressionToString(expr),
            value: val,
            run,
            runNameTruncationType,
            runOrGroupUniqueId: Run.uniqueId(run, groupKeys || []),
            runOrGroupDisplayName:
              groupKeys.length === 0
                ? run.displayName
                : Run.groupedRunDisplayName(run, groupKeys),
            customRunName,
          };
        });
      } else {
        return metrics.map(key => {
          const metricName = Run.keyDisplayName(key);
          return {
            run,
            value: Run.getValueSafe(run, key) as number,
            metricName,
            runOrGroupUniqueId: Run.uniqueId(
              run,
              groupKeys ?? [],
              !mergeRunsets
            ),
            runNameTruncationType,
            runOrGroupDisplayName:
              groupKeys.length === 0
                ? run.displayName
                : Run.groupedRunDisplayName(run, groupKeys),
            customRunName,
          };
        });
      }
    })
    .flat();

  if (groupKeys.length > 0 || metrics.length > 1) {
    const groupedBars = _.groupBy(
      barData,
      b =>
        // Can use not-null type assertion because we know we set l.run above.
        b.runOrGroupUniqueId
    );
    const bars = _.flatMap(groupedBars, barSet => {
      return aggregatePoints(
        barSet,
        aggregateCalculations,
        groupAgg ?? ChartAggOption.Mean,
        groupArea ?? 'none',
        groupKeys,
        violinPlot ? DEFAULT_VIOLIN_PLOT_BINS : undefined
      );
    }).filter((b): b is GroupDataPoint => b != null);
    barData = bars;
  }

  return barData;
}

function pointResultsForRunset(
  runs: RunsData['filtered'],
  {
    aggregateCalculations,
    aggregateMetrics,
    customRunNames,
    expressions,
    groupAgg,
    groupArea,
    groupBy,
    legendTemplate,
    metricKeys,
    panelAggregate,
    violinPlot,
  }: PointsFromDataProps,
  q: RunSetInfo | undefined,
  mergeRunsets: boolean
): RunDataPoint[] {
  const runSetID = q?.id;
  const runsForRunset =
    runSetID != null ? runs.filter(r => r.runsetInfo.id === runSetID) : runs;

  const groupKeys = panelAggregate
    ? [Run.key('config', groupBy ?? '')]
    : (q?.grouping ?? []);

  const partialParams = {
    runs: runsForRunset,
    groupKeys,
    aggregateCalculations,
    groupArea,
    groupAgg,
    violinPlot,
    legendTemplate,
    mergeRunsets,
    customRunNames,
    runNameTruncationType: q?.runNameTruncationType,
  };

  if (aggregateMetrics) {
    return pointsFromRunset({
      metrics: metricKeys,
      expressions,
      ...partialParams,
    });
  }

  return metricKeys.flatMap(y => {
    return pointsFromRunset({
      metrics: [y],
      expressions,
      ...partialParams,
    });
  });
}

const convertRunDataPointsToBars = (props: {
  dataPoints: Array<RunDataPoint | GroupDataPoint>;
  useRunName: boolean;
  useMetricName: boolean;
  legendTemplate: string;
  colorEachMetricDifferently: boolean;
  colorPaletteOption: ColorPaletteOption;
  customRunColors?: RunColorConfig;
  boxPlot?: boolean;
  violinPlot?: boolean;
}): Bar[] => {
  const {
    dataPoints,
    customRunColors,
    violinPlot,
    legendTemplate,
    colorPaletteOption,
  } = props;
  const metricToColorIdx = new Map<string, number>();
  if (props.colorEachMetricDifferently) {
    // build a map of metrics to indexes for coloring
    let maxIdx = 0;
    dataPoints.forEach(point => {
      if (!metricToColorIdx.has(point.metricName)) {
        metricToColorIdx.set(point.metricName, maxIdx);
        maxIdx++;
      }
    });
  }

  const bars = dataPoints.map(point => {
    const titleTemplate = parseLegendTemplate(
      legendTemplate,
      true,
      point.run,
      isGroupDataPoint(point) ? point.groupKeys : [],
      prettifyMetricName(point.metricName),
      point.customRunName
    );

    const key = legendTemplateRemoveCrosshairValues(titleTemplate).trim();

    const pointColor = props.colorEachMetricDifferently
      ? ColorUtil.color(metricToColorIdx.get(point.metricName) ?? 0)
      : // normal coloring
        point.run
        ? ColorUtil.runColor({
            run: point.run,
            groupKeys: isGroupDataPoint(point) ? point.groupKeys : undefined,
            customRunColors,
            colorPaletteOption,
          })
        : '000000';

    const uniqueId = point.runOrGroupUniqueId;

    return {
      bins: violinPlot && isGroupDataPoint(point) ? point.bins : undefined,
      color: pointColor,
      displayName: point.runOrGroupDisplayName,
      key,
      mean: isGroupDataPoint(point) ? point.mean : undefined,
      metricName: point.metricName,
      quartiles: isGroupDataPoint(point) ? point.quartiles : undefined,
      range: isGroupDataPoint(point) ? point.range : undefined,
      stddev: isGroupDataPoint(point) ? point.stddev : undefined,
      title: titleTemplate,
      uniqueId,
      value: point.value,
      runNameTruncationType: point.runNameTruncationType,
    };
  });

  return bars;
};

interface PointsFromDataProps {
  metricKeys: RunTypes.Key[];
  customRunColors?: RunColorConfig;
  customRunNames?: CustomRunNames;
  groupBy?: string;
  panelAggregate?: boolean;
  groupAgg?: ChartAggOption;
  groupArea?: AggregateCalculation;
  runSets?: RunSetInfo[];
  aggregateCalculations: AggregateCalculation[];
  colorEachMetricDifferently: boolean;
  aggregateMetrics: boolean;
  boxPlot?: boolean;
  violinPlot?: boolean;
  legendTemplate: string;
  expressions?: Expression[];
  colorPaletteOption: ColorPaletteOption;
}

export const getPointsFromData = (
  runs: RunsData['filtered'],
  props: PointsFromDataProps
) => {
  /* Convert runsdata to barchart data */

  const {
    boxPlot,
    colorEachMetricDifferently,
    customRunColors,
    customRunNames,
    legendTemplate,
    metricKeys,
    runSets,
    violinPlot,
    colorPaletteOption,
  } = props;

  if (runs.length === 0) {
    return [];
  }

  let barResults: RunDataPoint[];
  const runSetByID: {[id: string]: RunSetInfo} = {};
  if (runSets != null) {
    // When we have a runset (everywhere but the run page), map over the runsets.
    barResults = runSets.flatMap(runSet =>
      pointResultsForRunset(runs, props, runSet, false)
    );

    runSets.forEach(rs => (runSetByID[rs.id] = rs));
  } else {
    // Else we're in the run page and there's no runset.
    barResults = pointResultsForRunset(runs, props, undefined, false);
  }

  const useMetricName = metricKeys.length > 1;
  const useRunName =
    !useMetricName ||
    _.uniq(barResults.map(br => br.runOrGroupDisplayName)).length > 1;

  const bars = convertRunDataPointsToBars({
    boxPlot,
    colorEachMetricDifferently,
    customRunColors,
    dataPoints: barResults,
    legendTemplate: legendTemplate || '',
    useMetricName,
    useRunName,
    violinPlot,
    colorPaletteOption,
  });
  return bars;
};

const convertRunDataPointsToScalar = (props: {
  dataPoint: RunDataPoint | GroupDataPoint;
  useRunName: boolean;
  useMetricName: boolean;
  legendTemplate: string;
  customRunColors?: RunColorConfig;
  entityName?: string;
  projectName?: string;
  groupKeys: RunTypes.Key[];
  colorPaletteOption: ColorPaletteOption;
}): Scalar => {
  const {
    dataPoint,
    customRunColors,
    legendTemplate,
    groupKeys,
    colorPaletteOption,
  } = props;

  const rootUrl =
    props.entityName != null && props.projectName != null
      ? `/${props.entityName}/${props.projectName}/runs`
      : null;

  const titleTemplate = legendTemplateToFancyLegendProps(
    legendTemplate,
    dataPoint.run,
    groupKeys,
    prettifyMetricName(dataPoint.metricName),
    rootUrl ?? undefined
  );

  const pointColor = dataPoint.run
    ? ColorUtil.runColor({
        run: dataPoint.run,
        groupKeys,
        customRunColors,
        colorPaletteOption,
      })
    : '000000';
  const uniqueId = dataPoint.runOrGroupUniqueId;

  return {
    key: titleTemplate,
    color: pointColor,
    uniqueId,
    value: dataPoint.value,
    range: isGroupDataPoint(dataPoint) ? dataPoint.range : undefined,
    stddev: isGroupDataPoint(dataPoint) ? dataPoint.stddev : undefined,
    stderr: isGroupDataPoint(dataPoint) ? dataPoint.stderr : undefined,
  };
};

interface ScalarFromDataProps {
  metricKeys: RunTypes.Key[];
  customRunColors?: RunColorConfig;
  groupAgg?: ChartAggOption;
  groupArea?: AggregateCalculation;
  runSets?: RunSetInfo[];
  aggregateCalculations: AggregateCalculation[];
  legendTemplate: string;
  expressions?: Expression[];
  entityName?: string;
  projectName?: string;
  groupKeys: RunTypes.Key[];
  colorPaletteOption: ColorPaletteOption;
}

/* Convert runsdata to barchart data */
export const getScalarFromData = (
  runs: RunsData['filtered'],
  props: ScalarFromDataProps
): Scalar | null => {
  if (runs.length === 0) {
    return null;
  }
  const {metricKeys, customRunColors, legendTemplate} = props;

  const barResults = pointResultsForRunset(
    runs,
    {
      ...props,
      groupBy: '',
      panelAggregate: true,
      aggregateMetrics: true,
      colorEachMetricDifferently: false,
    },
    undefined,
    true
  );

  const useMetricName = metricKeys.length > 1;
  const useRunName =
    !useMetricName ||
    _.uniq(barResults.map(br => br.runOrGroupDisplayName)).length > 1;
  if (barResults.length === 0) {
    return {value: 0} as Scalar;
  }

  const runsetForRun = props.runSets?.find(
    rs => rs.id === barResults[0].run.runsetInfo.id
  );
  const entityName = runsetForRun?.entityName ?? props.entityName;
  const projectName = runsetForRun?.projectName ?? props.projectName;

  const scalar = convertRunDataPointsToScalar({
    dataPoint: barResults[0],
    useRunName,
    useMetricName,
    legendTemplate: legendTemplate || '',
    customRunColors,
    entityName,
    projectName,
    groupKeys: props.groupKeys,
    colorPaletteOption: props.colorPaletteOption,
  });
  return scalar;
};

type GetPlotMarginParams = {
  axisKeys?: {
    xAxis?: string;
    yAxis?: string;
    zAxis?: string;
  };
  axisDomain?: {
    yAxis?: number[];
    xAxis?: number[];
  };
  axisType?: {
    yAxis?: YAxisType;
    xAxis?: XAxisType;
  };
  axisValues?: {
    yAxis?: string[];
    xAxis?: string[];
  };
  tickTotal?: {
    yAxis?: number;
    xAxis?: number;
  };
  fontSize?: PlotFontSize;
  yAxisTickFormatter?: (value: number) => string;
  rotateXAxisTickLabels?: boolean;
  panelHeight?: number;
};

export type PlotMargin = Record<'top' | 'bottom' | 'left' | 'right', number>;

// In react-vis, the axes are rendered within the plot margins (i.e. not included in the plot width).
// This function calculate the margins dynamically, based on the size of the axis labels.
export const getPlotMargin = ({
  axisKeys = {},
  axisDomain = {},
  axisType = {},
  axisValues = {},
  tickTotal = {},
  fontSize = 'small',
  yAxisTickFormatter,
  rotateXAxisTickLabels = false,
  panelHeight = 100,
}: GetPlotMarginParams): PlotMargin => {
  const {xAxis, yAxis, zAxis} = axisKeys;
  const xIsTime = xAxis && Run.isTimeKeyString(xAxis);
  const yIsTime = yAxis && Run.isTimeKeyString(yAxis);
  const zIsTime = zAxis && Run.isTimeKeyString(zAxis);

  const marginDefaults: PlotMargin = {
    top: 8, // distance between the top of the plot and the bottom of the legend
    right: plotFontSizeToPx.axis[fontSize] + 2, // prevent rightmost axis label from getting cut off
    bottom: plotFontSizeToPx.axis[fontSize] + axisLabelMargin,
    left: 0,
  };
  const marginOverrides: Partial<PlotMargin> = {};

  if (axisValues?.yAxis != null) {
    marginOverrides.left = getYAxisWidth(axisValues.yAxis, fontSize);
  }

  if (axisDomain?.yAxis) {
    const args = {
      type: axisType.yAxis ?? 'linear',
      domain: axisDomain.yAxis,
    };

    // This is the internal function used by react vis to calculate the axis values
    const scale = getScaleFnFromScaleObject(args);

    const scaleTicks = scale.ticks(tickTotal?.yAxis);
    const formatFn = yIsTime ? Run.formatTimestamp : formatYAxis;

    marginOverrides.left = getYAxisWidth(
      scaleTicks.map(yAxisTickFormatter ?? formatFn),
      fontSize
    );
  }

  if (rotateXAxisTickLabels) {
    const args = {
      type: axisType.xAxis ?? 'linear',
      domain: axisDomain.xAxis ?? [],
    };

    // This is the internal function used by react vis to calculate the axis values
    const scale = getScaleFnFromScaleObject(args);

    const scaleTicks = scale.ticks(tickTotal?.xAxis);
    const formatFn = xIsTime
      ? Run.formatTimestamp
      : scale.tickFormat(scaleTicks.length); // This is the default format function for the scale type

    const xMargin = getAngledXAxisMarginSize(
      scaleTicks.map(formatFn),
      fontSize,
      panelHeight * 0.5,
      plotFontSizeToPx.axis[fontSize] + 12 // to account for the default lineHeight of the text
    );
    marginOverrides.bottom = xMargin.width + 10; // This is to add a buffer because of the tick itself
    // This is to make room on the left for the first tick's label since it's rotated
    // Multiply by 0.8 as a hack because we don't know where the first tick will be
    // but xMargin.width is the width of the longest tick label
    marginOverrides.left = Math.max(
      marginOverrides.left ?? marginDefaults.left,
      xMargin.width * 0.8
    );
  }

  if (zIsTime) {
    marginOverrides.top = 20;
  }

  return {
    ...marginDefaults,
    ...marginOverrides,
  };
};

export function getMetricIdentifiersFromExpressions(
  expressions?: Expression[],
  xExpression?: Expression
): {
  xExpressionMetricIdentifiers: string[];
  expressionMetricIdentifiers: string[];
} {
  const xExpressionMetricIdentifiers =
    xExpression != null ? metricsInExpression(xExpression) : [];
  const expressionMetricIdentifiers =
    expressions != null
      ? expressions.flatMap(expr => metricsInExpression(expr))
      : [];
  return {xExpressionMetricIdentifiers, expressionMetricIdentifiers};
}

export function getAllMetrics(
  metrics: string[],
  expressionMetricIdentifiers: string[],
  xExpressionMetricIdentifiers: string[]
): string[] {
  return _.uniq(
    _.concat(xExpressionMetricIdentifiers, expressionMetricIdentifiers, metrics)
  );
}

export const AXIS_TICK_ROTATE = -45;

export const getAngledXAxisMarginSize = (
  keys: string[],
  plotFontSize: PlotFontSize,
  maxTextWidth: number,
  textHeight: number
): {width: number; height: number} => {
  if (keys.length === 0) {
    return {width: 0, height: 0};
  }
  const w =
    15 +
    Math.min(
      maxTextWidth,
      Math.max(...keys.map(k => textWidth(k, plotFontSize)))
    );
  const axisTickRotateRadians = AXIS_TICK_ROTATE * (Math.PI / 180);
  // Convert the text width to height of the angled triangle with a standard trig operation
  // The width needs to be at least the height of the text so we can guarantee the text is visible no matter the angle
  return {
    width: w * Math.sin(Math.abs(axisTickRotateRadians)),
    height: Math.max(w * Math.cos(Math.abs(axisTickRotateRadians)), textHeight),
  };
};
